<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <complete-activation-steps
      class="row-span-2"
      :title="$t('$ActivateCampaigns.Title')"
      :subtitle="$t('$ActivateCampaigns.Text')"
      :chart-title="$t('$ActivateCampaigns.ChartTitle')"
      :steps="activateCampaignsStepsMapped"
      :height="180"
    />

    <complete-activation-steps
      class="row-span-3"
      :title="$t('$CampaignToolkit.Title')"
      :subtitle="$t('$CampaignToolkit.Text')"
      :steps="campaignToolkitStepsMapped"
      :height="300"
      accelerometer-style
    />

    <contacts-card-count-actions
      class="row-span-1"
      @count="(count) => contactsCount=count"/>

    <card-count-classified-list-campaigns
      class="row-span-3"
      @count="(count) => campaignsCount=count"/>

    <reviews-summary-unified
      class="row-span-4"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CompleteActivationSteps from '@/views/_components/CompleteActivationSteps.vue';
import ReviewsSummaryUnified from '@/views/_components/ReviewsSummaryUnified.vue';
import ContactsCardCountActions from '@/views/_components/ContactsCardCountActions.vue';
import CardCountClassifiedListCampaigns from '@/views/_components/CardCountClassifiedListSMSCampaigns.vue';
import smsCampaignService from '@/api/modules/campaigns/sms-campaign.service';

/**
 * Dashboard home
 *
 * @module views/Home
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'TheHome',
  i18n: {
    messages: {
      en: {
        $ActivateCampaigns: {
          Title: 'Activate your account',
          Text: 'Complete these steps to activate your account and start sending campaigns.',
          ChartTitle: 'Activation progress',
          $ActivationSteps: {
            $CompleteAccountDetails: {
              Title: 'Complete your account details',
              Subtitle: 'Finish setting up your account to unlock the platform\'s full potential.',
            },
            $AddOrImportContacts: {
              Title: 'Add or import your contacts',
              Subtitle: 'Upload your contact list to start engaging with your audience.',
            },
            $SendOrScheduleCampaign: {
              Title: 'Send or schedule your first campaign',
              Subtitle: 'Design, set up, and send your first campaign.',
            },
          },
        },
        $CampaignToolkit: {
          Title: 'Get started with campaigns',
          Text: 'A checklist to help you get the most out of our campaign tools.',
          $ActivationSteps: {
            $RequestGoogleReview: {
              Title: 'Request a Google review',
              Subtitle: 'Create a review link to request client feedback, manage negative responses, and boost your online reputation.',
            },
            $WaitingList: {
              Title: 'Create a waiting list',
              Subtitle: 'Set up a waiting list to manage and serve clients efficiently.',
            },
            $SendQuickMessage: {
              Title: 'Send a quick message',
              Subtitle: 'Send a personalised message to any contact instantly.',
            },
            $MarketingCampaign: {
              Title: 'Create a marketing campaign',
              Subtitle: 'Launch a compliant, targeted campaign to engage a large audience.',
            },
            $TransactionalCampaign: {
              Title: 'Create a transactional campaign',
              Subtitle: 'Keep clients informed with updates about their transactions.',
            },
          },
        },
      },
    },
  },
  components: {
    CompleteActivationSteps,
    ContactsCardCountActions,
    CardCountClassifiedListCampaigns,
    ReviewsSummaryUnified,
  },
  data() {
    return {
      contactsCount: 0,
      campaignsCount: 0,
      campaignsByMessageType: null,
      activateCampaignsSteps: [
        {
          title: this.$t('$ActivateCampaigns.$ActivationSteps.$CompleteAccountDetails.Title'),
          subtitle: this.$t('$ActivateCampaigns.$ActivationSteps.$CompleteAccountDetails.Subtitle'),
          route: 'account/details',
          icon: 'icon-user-check',
          completed: false,
        },
        {
          title: this.$t('$ActivateCampaigns.$ActivationSteps.$AddOrImportContacts.Title'),
          subtitle: this.$t('$ActivateCampaigns.$ActivationSteps.$AddOrImportContacts.Subtitle'),
          route: '/contacts',
          icon: 'icon-users',
          completed: false,
        },
        {
          title: this.$t('$ActivateCampaigns.$ActivationSteps.$SendOrScheduleCampaign.Title'),
          subtitle: this.$t('$ActivateCampaigns.$ActivationSteps.$SendOrScheduleCampaign.Subtitle'),
          route: '/sms/campaigns/create',
          icon: 'icon-send',
          completed: false,
        },
      ],

      campaignToolkitSteps: [
        {
          title: this.$t('$CampaignToolkit.$ActivationSteps.$RequestGoogleReview.Title'),
          subtitle: this.$t('$CampaignToolkit.$ActivationSteps.$RequestGoogleReview.Subtitle'),
          route: '/sms/campaigns/create',
          icon: 'icon-star',
          completed: false,
        },
        {
          title: this.$t('$CampaignToolkit.$ActivationSteps.$WaitingList.Title'),
          subtitle: this.$t('$CampaignToolkit.$ActivationSteps.$WaitingList.Subtitle'),
          route: '/waitlist',
          icon: 'icon-users',
          completed: false,
        },
        {
          title: this.$t('$CampaignToolkit.$ActivationSteps.$SendQuickMessage.Title'),
          subtitle: this.$t('$CampaignToolkit.$ActivationSteps.$SendQuickMessage.Subtitle'),
          route: '/sms/campaigns/quick',
          icon: 'icon-send',
          completed: false,
        },
        {
          title: this.$t('$CampaignToolkit.$ActivationSteps.$MarketingCampaign.Title'),
          subtitle: this.$t('$CampaignToolkit.$ActivationSteps.$MarketingCampaign.Subtitle'),
          route: '/sms/campaigns/create',
          icon: 'icon-message-circle',
          completed: false,
        },
        {
          title: this.$t('$CampaignToolkit.$ActivationSteps.$TransactionalCampaign.Title'),
          subtitle: this.$t('$CampaignToolkit.$ActivationSteps.$TransactionalCampaign.Subtitle'),
          route: '/sms/campaigns/create',
          icon: 'icon-message-square',
          completed: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getTenantFlag: 'auth/getTenantFlag',
      reviewsSettings: 'auth/tenantAccountSettingsReviews',
    }),
    waitingListConfigurationWasCompleted() {
      return this.getTenantFlag('completedAccountDetails')
        && this.getTenantFlag('completedWaitingListSettings')
        && this.getTenantFlag('completedBrandingSettings')
        && this.getTenantFlag('completedCompanyPoliciesSettings')
        && this.getTenantFlag('completedOpeningHoursSettings');
    },
    activateCampaignsStepsMapped() {
      const activateCampaignsSteps = [...this.activateCampaignsSteps];

      activateCampaignsSteps[0].completed = this.getTenantFlag('completedAccountDetails');
      activateCampaignsSteps[1].completed = this.contactsCount > 0;
      activateCampaignsSteps[2].completed = this.campaignsCount > 0;

      return activateCampaignsSteps;
    },
    campaignToolkitStepsMapped() {
      const campaignToolkitSteps = [...this.campaignToolkitSteps];

      campaignToolkitSteps[0].completed = this.campaignsByMessageType
        && this.campaignsByMessageType.review > 0;
      campaignToolkitSteps[1].completed = this.waitingListConfigurationWasCompleted;
      campaignToolkitSteps[2].completed = this.campaignsByMessageType
        && this.campaignsByMessageType.quick > 0;
      campaignToolkitSteps[3].completed = this.campaignsByMessageType
        && this.campaignsByMessageType.marketing > 0;
      campaignToolkitSteps[4].completed = this.campaignsByMessageType
        && this.campaignsByMessageType.transactional > 0;

      return campaignToolkitSteps;
    },
  },
  async created() {
    await this.fetchSMSCampaignsCountByMessageType();
  },
  methods: {
    async fetchSMSCampaignsCountByMessageType() {
      const resp = await smsCampaignService.getCountByMessageType();
      this.campaignsByMessageType = resp.data;
    },
  },
};
</script>

<style lang="scss" scoped>
.home-container {
  grid-template-rows: repeat(7, auto);
}
</style>
